/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useContext, useEffect, useRef } from 'react';
import { LiveChatContext } from '../../screens/LiveChatScreen';
import classNames from 'classnames';

// Component imports
import { Card, Col, Grid, Row } from 'antd';
import LiveChatMessenger from './LiveChatMessenger';
import LiveChatSidebarLeft from './LiveChatSidebarLeft';
import LiveChatSidebarRight from './LiveChatSidebarRight';

// Misc imports
import useContainerResize from '../../utils/hooks/useContainerResize';
import { STORAGE_KEYS } from '../../constants/main';
import useTheme from '../../utils/hooks/useTheme';

const LiveChatLayout = () => {
  const screens = Grid.useBreakpoint();
  const isTabletSize = screens.md && !screens.xl;
  const { livechatPanelHeight } = useTheme();

  const inboxRef = useRef(null);
  const sidebarRef = useRef(null);

  const { inboxOpen, isMobileSize, sidebarOpen, setSidebarOpen } = useContext(LiveChatContext);

  const { width: inboxWidth, setWidth: setInboxWidth, startResizing: startResizingInbox } = useContainerResize(inboxRef, 'inboxWidth', 380);
  const { width: sidebarWidth, setWidth: setSidebarWidth, startResizing: startResizingSidebar } = useContainerResize(sidebarRef, 'sidebarWidth', 340, true);

  const layoutConfig = window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_LAYOUT_PREFERENCES);

  // This is used to force a render-sync
  // between the two layout tabs
  // TODO: Refactor the tabs component tree
  useEffect(() => {
    if (!layoutConfig) {
      return;
    }
    const configObject = JSON.parse(layoutConfig);
    setSidebarWidth(configObject?.sidebarWidth);
    setInboxWidth(configObject?.inboxWidth);
  }, [layoutConfig]);

  useEffect(() => {
    if (isTabletSize && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [isTabletSize]);

  return (
    <Card
      className="overflow-hidden bordered p-0"
      bodyStyle={{
        minHeight: 480,
        height: livechatPanelHeight,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Row wrap={false} className="flex-auto overflow-hidden">
        <Col
          className={classNames('overflow-hidden', {
            'border-right': !isMobileSize
          })}
          ref={inboxRef}
          flex={`${isMobileSize ? (inboxOpen ? '1 1 auto' : '0') : `0 0 ${inboxWidth}px`}`}
          style={{
            maxWidth: isMobileSize ? '100%' : 520,
            minWidth: isMobileSize ? 0 : 340,
            width: inboxOpen ? isMobileSize ? '100%' : inboxWidth : 0
          }}
        >
          {
            !isMobileSize ? (
              <div
                className="resize-handle top-0 right-0"
                onMouseDown={startResizingInbox}
              />
            ) : null
          }

          <LiveChatSidebarLeft />
        </Col>

        <Col
          className="overflow-hidden"
          flex={`${isMobileSize ? (sidebarOpen || inboxOpen ? '0' : '1 1 auto') : '1 1 auto'}`}
          style={{
            minWidth: !isMobileSize ? 280 : 0
          }}
        >
          <LiveChatMessenger />
        </Col>

        <Col
          className={classNames('overflow-hidden h-100 bg-white', {
            'border-left': !isMobileSize
          })}
          flex={`${isMobileSize ? (sidebarOpen && !inboxOpen ? '1 1 auto' : '0') : (sidebarOpen ? `0 0 ${sidebarWidth}px` : '0')}`}
          style={{
            opacity: sidebarOpen ? 1 : 0,
            position: isTabletSize ? 'absolute' : 'relative',
            maxWidth: isMobileSize ? '100%' : isTabletSize ? 340 : 520,
            minWidth: sidebarOpen ? 340 : 0,
            width: sidebarOpen ? isMobileSize ? '100%' : sidebarWidth : 0,
            right: 0,
            zIndex: 1100
          }}
          ref={sidebarRef}
        >
          {
            !isMobileSize && !isTabletSize ? (
              <div
                className="resize-handle left-0 top-0"
                onMouseDown={startResizingSidebar}
              />
            ) : null
          }

          <LiveChatSidebarRight />
        </Col>
      </Row>
    </Card>
  );
};

export default LiveChatLayout;
